/**
 * Abschnitt „Ausstattung und Auslegung"
 *
 * Vier Merkmale, die der Anlagenhersteller vorgibt (ELiA 4.5, 14.3b.1, 16.1.3, 16.1.9), plus
 * die Richtfunk-Voranfrage bei der Bundesnetzagentur (16.1.1) – die gibt es nur je Anlage im
 * Projekt, nicht am Anlagentyp, deshalb der Schalter `mitRichtfunk`.
 *
 * Wird von der Anlagentyp-Verwaltung und vom Anlagen-Dialog gemeinsam benutzt, damit die
 * Gliederung nur einmal existiert.
 */
import React from "react";
import SubSection, { subSectionFullWidth } from "../../../layout/SubSection";
import InputField from "../../../common/inputs/InputField";
import InputFieldMultiline from "../../../common/inputs/InputFieldMultiline";
import ToggleChip from "../../../common/inputs/ToggleChip";
import { Box, Typography } from "@mui/material";
import { colors, fonts } from "../../../../styles/theme";
import { AusstattungFelder as AusstattungWerte } from "../../../../services/api/anlagentypen";

/** Nur an der Anlage: das Ergebnis der Voranfrage bei der Bundesnetzagentur (16.1.1) */
export interface RichtfunkWerte {
  richtfunkstrecke_betroffen?: boolean | null;
  bnetza_vorgangsnummer?: string | null;
}

interface AusstattungFelderProps {
  werte: Partial<AusstattungWerte & RichtfunkWerte>;
  onChange: (feld: string, wert: string | boolean | null) => void;
  /** Am Anlagentyp aus, im Projekt an – die Richtfunkstrecke haengt am Standort, nicht am Typ */
  mitRichtfunk?: boolean;
  readOnly?: boolean;
  defaultExpanded?: boolean;
}

const AusstattungFelder: React.FC<AusstattungFelderProps> = ({
  werte,
  onChange,
  mitRichtfunk = false,
  readOnly = false,
  defaultExpanded = false,
}) => (
  <SubSection
    title="Ausstattung und Auslegung"
    defaultExpanded={defaultExpanded}
    columns={2}
  >
    <InputField
      label="BNK-Funktionsart"
      value={werte.bnk_funktionsart ?? ""}
      onChange={(v) => onChange("bnk_funktionsart", v)}
      readOnly={readOnly}
      fullWidth
      tooltip="Art der bedarfsgesteuerten Nachtkennzeichnung. Speist ELiA 16.1.9 und den Erläuterungstext in 16.1.7."
    />
    <InputField
      label="Auslegungsklasse"
      value={werte.auslegungsklasse ?? ""}
      onChange={(v) => onChange("auslegungsklasse", v)}
      readOnly={readOnly}
      fullWidth
      placeholder="z. B. IEC IIA"
      tooltip="Auslegungsklasse nach IEC 61400. Deckt in ELiA 14.3b.1 die klimabedingten Risiken ab."
    />
    <Box sx={subSectionFullWidth}>
      <InputFieldMultiline
        label="Schallmindernde Ausstattung"
        value={werte.schallmindernde_ausstattung ?? ""}
        onChange={(v) => onChange("schallmindernde_ausstattung", v)}
        readOnly={readOnly}
        fullWidth
        placeholder="z. B. Hinterkantenkamm an den Rotorblättern"
        minRows={2}
        tooltip="Speist den erzeugten Erläuterungstext zu den Schallschutzmaßnahmen in ELiA 4.5."
      />
    </Box>
    <Box sx={subSectionFullWidth}>
      <InputFieldMultiline
        label="Sicherheitstechnische Ausstattung"
        value={werte.sicherheitstechnische_ausstattung ?? ""}
        onChange={(v) => onChange("sicherheitstechnische_ausstattung", v)}
        readOnly={readOnly}
        fullWidth
        placeholder="z. B. Blitzschutz, Eiserkennung, Eisabwurf-Abschaltung"
        minRows={2}
        tooltip="Speist den erzeugten Erläuterungstext in ELiA 16.1.3."
      />
    </Box>

    {mitRichtfunk && (
      <>
        <Box sx={subSectionFullWidth}>
          <Typography
            sx={{
              fontFamily: fonts.primary,
              fontSize: "0.75rem",
              color: colors.text.secondary,
              mt: 1,
            }}
          >
            Richtfunk — Ergebnis der Voranfrage bei der Bundesnetzagentur. ELiA
            fragt je Anlage, auch wenn die Voranfrage für den ganzen Park läuft.
          </Typography>
        </Box>
        <ToggleChip
          value={werte.richtfunkstrecke_betroffen === true}
          activeLabel="Richtfunkstrecke betroffen"
          inactiveLabel="Richtfunkstrecke betroffen"
          disabled={readOnly}
          onToggle={() =>
            onChange(
              "richtfunkstrecke_betroffen",
              werte.richtfunkstrecke_betroffen === true ? false : true,
            )
          }
        />
        <InputField
          label="AZ/Vorgangsnr. der Bundesnetzagentur"
          value={werte.bnetza_vorgangsnummer ?? ""}
          onChange={(v) => onChange("bnetza_vorgangsnummer", v)}
          readOnly={readOnly}
          fullWidth
        />
      </>
    )}
  </SubSection>
);

export default AusstattungFelder;
